/* eslint-disable @typescript-eslint/no-explicit-any */

import { useApi } from '@omnipresentgroup/api';
import { HTTPStatusCodes } from '@omnipresentgroup/constants';
import type {
  UpdateXeroAccountCodeMappingsRequest,
  UpdateXeroAccountCodeMappingsResponse,
  UpdateXeroAccountCodeMappingsResponseAPI,
} from '@omnipresentgroup/types';
import {
  type UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type { WretchResponse } from 'wretch/types';

import {
  XERO_INTEGRATION_API_BASE,
  XERO_INTEGRATION_API_PATH,
  XERO_INTEGRATION_XERO_ACCOUNT_CODE_MAPPINGS_PATH,
} from '../constants';
import {
  transformUpdateXeroAccountCodeMappingsResponseFromAPI,
  transformXeroAccountCodeMappingsPayloadToAPI,
} from '../mappers';
import { getXeroAccountCodeMappingsKey } from './useXeroAccountCodeMappings';
import { getXeroConnectionKey } from './useXeroConnection';

export const useXeroAccountCodeMappingsMutation = ({
  clientId,
  onSuccess,
  ...options
}: Omit<
  UseMutationOptions<
    WretchResponse,
    unknown,
    UpdateXeroAccountCodeMappingsRequest,
    {
      data: UpdateXeroAccountCodeMappingsResponse;
    }
  >,
  'mutationFn'
> & {
  clientId: string;
}) => {
  const { PATCH } = useApi();

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateXeroAccountCodeMappingsRequest) =>
      PATCH({
        path: `${XERO_INTEGRATION_API_BASE}/${XERO_INTEGRATION_API_PATH}/${clientId}/${XERO_INTEGRATION_XERO_ACCOUNT_CODE_MAPPINGS_PATH}`,
        body: transformXeroAccountCodeMappingsPayloadToAPI(payload),
      }),
    onSuccess: async (response, variables, context) => {
      const dataAPI =
        (await response.json()) as UpdateXeroAccountCodeMappingsResponseAPI;
      const data =
        transformUpdateXeroAccountCodeMappingsResponseFromAPI(dataAPI);

      if (response.status !== HTTPStatusCodes.MULTI_STATUS) {
        queryClient.invalidateQueries({
          queryKey: getXeroAccountCodeMappingsKey(clientId),
          refetchType: 'all',
        });
        queryClient.invalidateQueries({
          queryKey: getXeroConnectionKey(clientId),
          refetchType: 'all',
        });
      }

      onSuccess?.(response, variables, {
        ...context,
        data,
      });
    },
    ...options,
  });
};
